/**
 * CustomActionButtonBar — render własnych przycisków nawigacyjnych z Kreatora
 * Listy z pozycją „Pasek narzędzi". Akcje frontowe: navigate / open_url / refresh
 * (dispatch przez `runCustomAction`). Renderuje go wspólny hook akcji listy
 * (`useListActions` → `ListActionsMenu.toolbar`) na każdej liście z menu „Akcje".
 *
 * `CustomRowMenuItems` — pozycje „Akcja wiersza" w kebabach list z własnym menu
 * wiersza (Pracownicy, Użytkownicy), które nie używają wspólnego `RowActionsMenu`.
 */

import type { CustomActionButton } from '@saas/shared';

import { Button } from '@/components/Button';

import { type CustomActionContext, resolveIcon, runCustomAction } from './customButtonActions';
import type { RowActionItem } from './RowActionsMenu';

export function CustomRowMenuItems({
  items,
  onAfterSelect,
}: {
  items: RowActionItem[];
  onAfterSelect: () => void;
}): React.ReactElement | null {
  if (items.length === 0) return null;
  return (
    <>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Button
            key={item.key}
            variant="ghost"
            size="sm"
            role="menuitem"
            className="w-full justify-start rounded-none px-3 py-2"
            leftIcon={Icon ? <Icon size={14} aria-hidden="true" /> : undefined}
            onClick={() => {
              item.onSelect();
              onAfterSelect();
            }}
            data-testid={item.testId}
          >
            {item.label}
          </Button>
        );
      })}
      <div className="my-1 border-t border-border-secondary" />
    </>
  );
}

/** Mapuje wariant z kontraktu na wariant komponentu Button (outline → secondary). */
export function toButtonVariant(
  v: CustomActionButton['variant'],
): 'primary' | 'secondary' | 'ghost' | 'danger' {
  if (v === 'primary' || v === 'ghost' || v === 'danger') return v;
  return 'secondary';
}

export function CustomActionButtonBar({
  buttons,
  ctx,
  testIdPrefix,
}: {
  buttons: CustomActionButton[];
  ctx: CustomActionContext;
  testIdPrefix: string;
}): React.ReactElement | null {
  if (buttons.length === 0) return null;
  return (
    <>
      {buttons.map((btn) => {
        const Icon = resolveIcon(btn.icon);
        return (
          <Button
            key={btn.id}
            variant={toButtonVariant(btn.variant)}
            size="sm"
            leftIcon={Icon ? <Icon className="h-4 w-4" /> : undefined}
            onClick={() => runCustomAction(btn.action, ctx)}
            data-testid={`${testIdPrefix}-custom-${btn.id}`}
          >
            {btn.label}
          </Button>
        );
      })}
    </>
  );
}
